import React, { useEffect, useState } from 'react';
import { Star, X } from 'lucide-react';

/**
 * Non-intrusive popup that asks the user to leave a Google review.
 * Appears in the bottom-right corner after:
 *   - the visitor has opened the site at least 2 times (tracked via localStorage)
 *   - they have spent 8 s on the current page (so they've had time to engage)
 * Dismissal memory:
 *   - ✕ (close)        → hidden for 30 days
 *   - "Plus tard"      → hidden for 7 days
 *   - "Laisser un avis" → hidden for 1 year (we consider it done)
 */

// Direct Google "Write a review" URL for Pharmacie Saint Ange Des Amis.
// CID 0x590d810c976b53b7 corresponds to the pharmacy's Google Business Profile.
// If Google ever changes the CID, replace it below.
const GOOGLE_REVIEW_URL =
  'https://search.google.com/local/writereview?placeid=0x590d810c976b53b7';

const VISITS_KEY = 'sada_visit_count';
const HIDE_UNTIL_KEY = 'sada_review_popup_hide_until';
const MIN_VISITS = 2;
const SHOW_DELAY_MS = 8000;

function nowTs() {
  return Date.now();
}

function isHidden() {
  try {
    const raw = localStorage.getItem(HIDE_UNTIL_KEY);
    if (!raw) return false;
    return Number(raw) > nowTs();
  } catch {
    return false;
  }
}

function hideFor(days) {
  try {
    localStorage.setItem(HIDE_UNTIL_KEY, String(nowTs() + days * 86400000));
  } catch {
    /* ignore */
  }
}

function incrementVisitCount() {
  try {
    const current = Number(localStorage.getItem(VISITS_KEY) || '0');
    const next = current + 1;
    localStorage.setItem(VISITS_KEY, String(next));
    return next;
  } catch {
    return 0;
  }
}

const GoogleReviewPopup = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // bump visit count once per mount of the homepage
    const visits = incrementVisitCount();
    if (visits < MIN_VISITS) return;
    if (isHidden()) return;

    const t = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  if (!visible) return null;

  const handleClose = () => {
    hideFor(30);
    setVisible(false);
  };

  const handleLater = () => {
    hideFor(7);
    setVisible(false);
  };

  const handleReview = () => {
    hideFor(365);
    setVisible(false);
    window.open(GOOGLE_REVIEW_URL, '_blank', 'noopener,noreferrer');
  };

  return (
    <div
      className="fixed bottom-4 left-4 md:bottom-6 md:left-6 z-[9999] w-[min(360px,calc(100vw-2rem))] animate-slide-up"
      data-testid="google-review-popup"
    >
      <div className="relative rounded-2xl bg-white shadow-[0_12px_40px_rgba(0,0,0,0.15)] border border-neutral-200 p-4 pr-8">
        <button
          onClick={handleClose}
          aria-label="Fermer"
          data-testid="google-review-popup-close"
          className="absolute top-2 right-2 p-1 rounded-full text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-center gap-1 mb-2">
          {[...Array(5)].map((_, i) => (
            <Star
              key={i}
              className="w-4 h-4 fill-[#FBBC04] text-[#FBBC04]"
            />
          ))}
          <span className="ml-2 text-xs font-semibold text-neutral-500 uppercase tracking-wide">
            Avis Google
          </span>
        </div>

        <p className="text-sm font-semibold text-neutral-900 mb-1">
          Vous appréciez notre pharmacie&nbsp;?
        </p>
        <p className="text-xs text-neutral-600 leading-relaxed mb-3">
          Un petit avis Google nous aide énormément à servir mieux la
          communauté d'Angré&nbsp;🙏
        </p>

        <div className="flex items-center gap-2">
          <button
            onClick={handleReview}
            data-testid="google-review-popup-cta"
            className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[#2E7D32] hover:bg-[#1B5E20] text-white text-sm font-semibold px-3 py-2 rounded-lg transition-colors"
          >
            <Star className="w-4 h-4 fill-white" />
            Laisser un avis
          </button>
          <button
            onClick={handleLater}
            data-testid="google-review-popup-later"
            className="text-xs text-neutral-500 hover:text-neutral-800 px-2 py-2 whitespace-nowrap"
          >
            Plus tard
          </button>
        </div>
      </div>
    </div>
  );
};

export default GoogleReviewPopup;
